/**
 * useTranslatedSlug - Language Switch Slug Lookup
 * 
 * Looks up the translated slug for an entity in a target locale.
 * Used by the language switcher to compute the target URL.
 * 
 * INV-4: Language switching uses sourceId to find translated slugs
 * INV-9: Publications have translated slugs, expert vanity URLs are constant
 * 
 * @example
 * // In language selector
 * const { translatedSlug } = useTranslatedSlug('location', '45', 'pt-BR');
 * // translatedSlug = 'brasil' (for Brazil in Portuguese)
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/utils/logger';
import type { RoutedSourceType } from './useSlugResolver';

// ============================================================================
// TYPES
// ============================================================================

export interface UseTranslatedSlugResult {
  /** The translated slug, or null if not found (fail-closed) */
  translatedSlug: string | null;
  isLoading: boolean;
  error: Error | null;
}

// ============================================================================
// CORE LOOKUP FUNCTION
// ============================================================================

/**
 * Look up translated slug for an entity in a target locale
 */
async function lookupTranslatedSlug(
  sourceType: RoutedSourceType,
  sourceId: string,
  targetLocale: string
): Promise<string | null> {
  const startTime = performance.now();

  if (!sourceType || !sourceId || !targetLocale) {
    return null;
  }

  // Special case: profile/expert slugs are constant across locales
  // The vanity URL doesn't change with language
  if (sourceType === 'profile') {
    return sourceId; // sourceId IS the vanity URL for profiles
  }

  try {
    // CRITICAL: Resolve regional locale to primary locale for content lookup
    // UI uses regional locales ('en-US', 'es-MX') but content DB uses primary ('en', 'es')
    // Special handling for zh-TW/zh-HK which map to 'zh-TW' primary
    let primaryLocale: string;
    if (targetLocale === 'zh-TW' || targetLocale === 'zh-HK') {
      primaryLocale = 'zh-TW'; // Traditional Chinese
    } else if (targetLocale.includes('-')) {
      primaryLocale = targetLocale.split('-')[0]; // 'en-US' → 'en'
    } else {
      primaryLocale = targetLocale;
    }

    // For routes, we need to match by source_id (the route identifier like 'experts', 'topics')
    // For other entities, source_id is a UUID
    const { data, error } = await supabase
      .from('localization_metadata')
      .select('translated_slug')
      .eq('source_type', sourceType)
      .eq('source_id', sourceId)
      .eq('locale', primaryLocale)  // Use primary locale for content lookup
      .maybeSingle();

    if (error) {
      logger.warn('[useTranslatedSlug] DB query failed', {
        sourceType,
        sourceId,
        targetLocale,
        error: error.message,
      });
      return null;
    }

    if (process.env.NODE_ENV === 'development') {
      logger.debug('[useTranslatedSlug] Lookup result', {
        sourceType,
        sourceId,
        targetLocale,
        translatedSlug: data?.translated_slug || null,
        timeMs: Math.round(performance.now() - startTime),
      });
    }

    return data?.translated_slug || null;
  } catch (err) {
    logger.error('[useTranslatedSlug] Unexpected error', {
      sourceType,
      sourceId,
      targetLocale,
      error: err,
    });
    return null;
  }
}

// ============================================================================
// HOOK
// ============================================================================

/**
 * Hook to look up translated slug for language switching
 * 
 * @param sourceType - Entity type ('route', 'page', 'location', etc.)
 * @param sourceId - Entity's source ID (UUID or identifier like 'experts')
 * @param targetLocale - Target locale code (e.g., 'pt-BR', 'de-DE')
 * @returns Translated slug or null if not found
 * 
 * @example
 * // Look up Brazilian location slug in German
 * const { translatedSlug } = useTranslatedSlug('location', '45', 'de-DE');
 * // translatedSlug = 'brasilien'
 */
export function useTranslatedSlug(
  sourceType: RoutedSourceType | null,
  sourceId: string | null,
  targetLocale: string | null
): UseTranslatedSlugResult {
  const {
    data: translatedSlug,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['translated-slug', sourceType, sourceId, targetLocale],
    queryFn: () => lookupTranslatedSlug(sourceType!, sourceId!, targetLocale!),
    enabled: !!sourceType && !!sourceId && !!targetLocale,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });

  // Handle disabled state
  if (!sourceType || !sourceId || !targetLocale) {
    return {
      translatedSlug: null,
      isLoading: false,
      error: null,
    };
  }

  return {
    translatedSlug: translatedSlug ?? null,
    isLoading,
    error: error as Error | null,
  };
}

// ============================================================================
// IMPERATIVE API
// ============================================================================

/**
 * Imperative slug translation for use outside React components
 * (e.g., in language switch handlers)
 */
export async function fetchTranslatedSlug(
  sourceType: RoutedSourceType,
  sourceId: string,
  targetLocale: string
): Promise<string | null> {
  return lookupTranslatedSlug(sourceType, sourceId, targetLocale);
}

// ============================================================================
// BATCH LOOKUP (for prefetching)
// ============================================================================

/**
 * Look up translated slugs for multiple locales at once
 * Useful for prefetching language switcher options
 */
export async function fetchTranslatedSlugsForAllLocales(
  sourceType: RoutedSourceType,
  sourceId: string
): Promise<Map<string, string>> {
  const results = new Map<string, string>();

  if (!sourceType || !sourceId) {
    return results;
  }

  // Profile slugs are constant
  if (sourceType === 'profile') {
    // Return empty map - profile URLs don't change
    return results;
  }

  try {
    const { data, error } = await supabase
      .from('localization_metadata')
      .select('locale, translated_slug')
      .eq('source_type', sourceType)
      .eq('source_id', sourceId);

    if (error) {
      logger.warn('[useTranslatedSlug] Batch lookup failed', {
        sourceType,
        sourceId,
        error: error.message,
      });
      return results;
    }

    if (data) {
      for (const row of data) {
        results.set(row.locale, row.translated_slug);
      }
    }

    return results;
  } catch (err) {
    logger.error('[useTranslatedSlug] Batch lookup error', {
      sourceType,
      sourceId,
      error: err,
    });
    return results;
  }
}
